import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { ArrowLeft, Search, Sparkles, Loader2, BookOpen, Calculator, Feather, Beaker, Type } from 'lucide-react';
import { toast } from 'sonner';

const MODULE_META = {
  alphabets: { label: 'Phonics',  icon: Type,      accent: 'from-teal-500 to-cyan-500' },
  reading:   { label: 'Reading',  icon: BookOpen,  accent: 'from-amber-500 to-orange-500' },
  math:      { label: 'Math',     icon: Calculator,accent: 'from-rose-500 to-red-500' },
  writing:   { label: 'Writing',  icon: Feather,   accent: 'from-purple-500 to-fuchsia-500' },
  science:   { label: 'Science',  icon: Beaker,    accent: 'from-emerald-500 to-teal-500' },
};

const gradeFromChild = (child) => {
  if (!child) return 'K';
  const explicit = (child.grade_level || '').toString().trim().toUpperCase();
  if (explicit) {
    if (['PK', 'PRE-K', 'PREK'].includes(explicit)) return 'PK';
    if (['K', 'KG', 'KINDERGARTEN'].includes(explicit)) return 'K';
    const digits = explicit.replace(/[^0-9]/g, '');
    if (digits) return digits;
  }
  if (child.birth_date) {
    try {
      const bd = new Date(child.birth_date);
      const now = new Date();
      const m = (now.getFullYear() - bd.getFullYear()) * 12 + (now.getMonth() - bd.getMonth());
      if (m < 60)  return 'PK';
      if (m < 72)  return 'K';
      if (m < 84)  return '1';
      if (m < 96)  return '2';
      if (m < 108) return '3';
      if (m < 120) return '4';
      if (m < 132) return '5';
      if (m < 144) return '6';
      return '7';
    } catch { /* noop */ }
  }
  return 'K';
};

const ExploreLearnPage = () => {
  const { childId } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const [child, setChild] = useState(null);
  const [selectedModule, setSelectedModule] = useState(null);
  const [catalog, setCatalog] = useState({});
  const [loading, setLoading] = useState(true);
  const [peekAhead, setPeekAhead] = useState(false);
  const [search, setSearch] = useState('');

  const grade = useMemo(() => gradeFromChild(child), [child]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/children/${childId}`);
        setChild(r.data);
      } catch { toast.error('Could not load child'); }
    })();
  }, [childId, api]);

  useEffect(() => {
    if (!child) return;
    setLoading(true);
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/learn/explore-catalog?grade=${grade}&peek_ahead=${peekAhead}`)
      .then((r) => setCatalog(r.data.catalog || {}))
      .catch(() => toast.error('Could not load topic catalog'))
      .finally(() => setLoading(false));
  }, [child, grade, peekAhead]);

  const filteredConcepts = useMemo(() => {
    const q = search.trim().toLowerCase();
    const items = selectedModule ? (catalog[selectedModule] || []) : Object.values(catalog).flat();
    if (!q) return items;
    return items.filter((c) => c.title.toLowerCase().includes(q) || c.concept_key.toLowerCase().includes(q));
  }, [search, selectedModule, catalog]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-teal-50/60 via-white to-slate-50" data-testid="explore-learn-page">
      <header className="glass border-b border-white/40 sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="explore-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="font-fredoka text-xl font-bold text-slate-900">Explore & Learn</h1>
            <p className="text-xs text-slate-500">Pick any topic — Zazu will teach it at your grade.</p>
          </div>
          <span className="inline-flex items-center gap-1 rounded-full bg-teal-50 border border-teal-200 text-teal-800 text-xs font-semibold px-2 py-0.5" data-testid="explore-grade-chip">
            Grade: {grade}
          </span>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Search + peek-ahead */}
        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search topics like 'vowels', 'fractions', 'water cycle'…"
              className="pl-9 h-11 rounded-full bg-white"
              data-testid="explore-search-input"
            />
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-slate-700 select-none">
            <Switch checked={peekAhead} onCheckedChange={setPeekAhead} data-testid="explore-peek-ahead-toggle" />
            Try a harder topic
          </label>
        </div>

        {/* Module tiles */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="explore-module-tiles">
          {Object.entries(MODULE_META).map(([key, m]) => {
            const Icon = m.icon;
            const active = selectedModule === key;
            const count = (catalog[key] || []).length;
            return (
              <button
                key={key}
                onClick={() => setSelectedModule(active ? null : key)}
                data-testid={`explore-module-${key}`}
                className={`relative rounded-2xl p-3 text-left transition-all border-2 ${
                  active ? 'border-teal-500 shadow-lg' : 'border-transparent bg-white shadow-sm hover:shadow-md'
                }`}
              >
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${m.accent} flex items-center justify-center mb-2`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <p className="font-semibold text-slate-800 text-sm">{m.label}</p>
                <p className="text-[11px] text-slate-500">{count} topic{count === 1 ? '' : 's'}</p>
              </button>
            );
          })}
        </div>

        {/* Concept catalog */}
        <div>
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-teal-500" />
            </div>
          ) : filteredConcepts.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <p>No topics match your search yet. Try turning on <strong>Try a harder topic</strong>.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3" data-testid="explore-concept-list">
              <AnimatePresence>
                {filteredConcepts.map((c) => {
                  const modOf = Object.keys(catalog).find((k) => (catalog[k] || []).some((x) => x.concept_key === c.concept_key && x.title === c.title));
                  return (
                    <motion.div
                      key={`${modOf}:${c.concept_key}`}
                      layout
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ type: 'spring', stiffness: 260, damping: 22 }}
                    >
                      <Link
                        to={`/explore/${childId}/${modOf}/${c.concept_key}`}
                        data-testid={`explore-concept-${modOf}-${c.concept_key}`}
                      >
                        <Card className="hover:shadow-md hover:-translate-y-0.5 transition-all border-l-4 border-l-teal-400">
                          <CardContent className="p-4 flex items-center gap-3">
                            <div className="w-12 h-12 rounded-xl bg-teal-50 border border-teal-100 flex items-center justify-center text-teal-700 font-fredoka font-bold whitespace-pre-line text-center leading-tight text-sm">
                              {(c.illustration || '✨').split('\n')[0].slice(0, 6)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-slate-800 truncate">{c.title}</p>
                              <p className="text-xs text-slate-500">
                                {MODULE_META[modOf]?.label || modOf}
                                {c.above_band && (
                                  <span className="ml-2 inline-flex items-center gap-1 text-amber-700">
                                    <Sparkles className="w-3 h-3" /> One grade ahead
                                  </span>
                                )}
                              </p>
                            </div>
                          </CardContent>
                        </Card>
                      </Link>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ExploreLearnPage;
